import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '@auth0/auth0-react';
import ExcelEditor, { SheetMap } from './ExcelEditor';
import UnifiedDocumentEditor from '../UnifiedDocumentEditor';
import SafePdfViewer from '../../shared/SafePdfViewer';
import UnsavedChangesModal from './UnsavedChangesModal';
import FileTypeSkeleton from './FileTypeSkeleton';
import { htmlToDocx, blobToBase64 } from '../../../utils/docxHandler';

/**
 * Extract body content and styles from a full HTML document
 * Returns { bodyHtml, styleSheet }
 */
function extractHtmlParts(fullHtml: string): { bodyHtml: string; styleSheet?: string } {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(fullHtml, 'text/html');

    // Extract styles from head
    const styleElement = doc.querySelector('head style');
    const styleSheet = styleElement?.textContent || '';

    // Extract body content
    const bodyElement = doc.querySelector('body');
    const bodyHtml = bodyElement?.innerHTML || fullHtml;

    return { bodyHtml, styleSheet };
  } catch (error) {
    console.error('Error parsing HTML document:', error);
    // If parsing fails, return the original HTML
    return { bodyHtml: fullHtml };
  }
}

interface SecureFileViewerProps {
  fileData: {
    FileID: string;
    FileName: string;
    FileType?: string;
    decryptedBlobUrl?: string;
    FileSize?: number;
    FolderID?: string;
  };
  isLoading: boolean;
  onSave?: (content: string, fileName: string) => Promise<void>;
  onDelete?: () => Promise<void>;
  onClose?: () => void;
  onVersionChange?: (versionId: string) => void;
  onShowVersionHistory?: () => void;
  onDownload?: () => void;
  onToggleEditMode?: () => void;
  /** Reading zoom factor from the viewer chrome, 1 = 100%. */
  zoom?: number;
  versions?: Array<{
    id: string;
    timestamp: string;
    author?: string;
  }>;
}

const SecureFileViewer: React.FC<SecureFileViewerProps> = ({
  fileData,
  isLoading,
  onSave,
  onClose,
  versions = [],
  zoom = 1
}) => {
  const { t } = useTranslation();
  const { getAccessTokenSilently } = useAuth0();
  const [editMode, setEditMode] = useState(false);
  const [editContent, setEditContent] = useState<string>('');
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const contentLoadedRef = useRef(false);
  const excelPublishedRef = useRef(false);

  // Excel editing state. `excelSheets` is EVERY sheet in the workbook, kept so
  // saving an edit to sheet 2 does not write sheet 2 out as the whole file.
  const [excelSheets, setExcelSheets] = useState<SheetMap>({});
  const [excelSheetOrder, setExcelSheetOrder] = useState<string[]>([]);
  const [excelHasChanges, setExcelHasChanges] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);


  // Reset content tracking when file changes
  useEffect(() => {
    contentLoadedRef.current = false;
    excelPublishedRef.current = false;
    setEditContent('');
    setEditMode(false);
    setExcelHasChanges(false);
  }, [fileData.FileID, fileData.FolderID]);

  // Cleanup on unmount and when file changes
  useEffect(() => {
    return () => {
      contentLoadedRef.current = false;
      // Scoped styles are now part of the content, so no global cleanup needed
    };
  }, [fileData.FileID]);

  // Handle unsaved changes warning
  useEffect(() => {
    if (!excelHasChanges) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [excelHasChanges]);



  // Determine file type based on MIME type and filename
  const getFileExtension = (): string => {
    const fileName = fileData.FileName.toLowerCase();
    const mimeType = (fileData.FileType || '').toLowerCase();

    // Check MIME types first (more reliable)
    if (mimeType.includes('pdf') || fileName.endsWith('.pdf')) {
      return 'pdf';
    }

    // Excel MIME types (must check before generic 'document' checks)
    if (
      mimeType.includes('spreadsheet') ||
      mimeType.includes('sheet') ||
      mimeType.includes('excel') ||
      mimeType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml') ||
      mimeType.includes('application/vnd.ms-excel') ||
      fileName.endsWith('.xlsx') ||
      fileName.endsWith('.xls') ||
      fileName.endsWith('.csv')
    ) {
      /*
        A CSV opens in the sheet grid, like an XLSX.

        It used to classify as 'text', and text content was only ever fetched
        inside `handleEditStart` — the view-mode effect loads DOCX only — so a
        CSV rendered an empty editor until you pressed Edit. `ExcelEditor`
        already reads a CSV blob into rows, so the grid is both the fix and the
        better reading of the file. Saving still writes CSV (see `handleSave`).
      */
      return 'excel';
    }

    // Word/Document MIME types
    if (
      mimeType.includes('word') ||
      mimeType.includes('document') ||
      mimeType.includes('application/vnd.openxmlformats-officedocument.wordprocessingml') ||
      mimeType.includes('application/msword') ||
      fileName.endsWith('.docx') ||
      fileName.endsWith('.doc')
    ) {
      return 'docx';
    }

    // Text files
    if (
      mimeType.includes('text') ||
      fileName.endsWith('.txt') ||
      fileName.endsWith('.csv')
    ) {
      return 'text';
    }

    return 'unknown';
  };

  const fileType = getFileExtension();
  const canEdit = fileType === 'docx' || fileType === 'text' || fileType === 'excel';
  const isPdf = fileType === 'pdf';
  const canView = canEdit || isPdf; // Can view in unified editor

  // Load DOCX HTML from blob or stream endpoint
  const loadDocxFromStream = useCallback(async () => {
    try {
      let htmlResponse: string;

      // First, try to use the blob URL if available (already fetched by WordEditor)
      if (fileData.decryptedBlobUrl) {
        const blobResponse = await fetch(fileData.decryptedBlobUrl);
        if (!blobResponse.ok) {
          throw new Error(`Failed to fetch blob: ${blobResponse.status}`);
        }
        const htmlBlob = await blobResponse.blob();
        // Check with includes() — blob type may contain charset, e.g. "text/html; charset=utf-8"
        if (htmlBlob.type.includes('text/html') || htmlBlob.type.includes('text/plain')) {
          htmlResponse = await htmlBlob.text();
        } else {
          throw new Error(`Invalid blob type: ${htmlBlob.type}`);
        }
      } else {
        // Fallback: call stream-file endpoint directly
        const token = await getAccessTokenSilently();
        const response = await fetch('/files/stream-file', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            FileID: fileData.FileID,
            FolderID: fileData.FolderID || ''
          })
        });

        if (!response.ok) {
          throw new Error(`Failed to stream DOCX: ${response.status}`);
        }

        htmlResponse = await response.text();
      }

      if (!htmlResponse || htmlResponse.trim().length === 0) {
        throw new Error('Empty HTML content');
      }

      // Extract body HTML and styles from the full document
      const { bodyHtml, styleSheet } = extractHtmlParts(htmlResponse);

      // For iframe rendering, we need to return a complete HTML document
      // The iframe will handle all styles in isolation
      if (styleSheet && styleSheet.trim().length > 0) {
        // Reconstruct the full HTML document for iframe
        const fullDocumentHtml = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    ${styleSheet}
  </style>
</head>
<body>
  ${bodyHtml}
</body>
</html>`;

        setEditContent(fullDocumentHtml);
      } else {
        // Fallback: wrap body HTML in minimal HTML structure
        const fullDocumentHtml = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
</head>
<body>
  ${bodyHtml}
</body>
</html>`;

        setEditContent(fullDocumentHtml);
      }

      return true;
    } catch (error) {
      console.error('[DOCX Stream Error]:', error);
      return false;
    }
  }, [fileData.FileID, fileData.FolderID, fileData.decryptedBlobUrl, getAccessTokenSilently]);

  // Auto-trigger DOCX loading on mount/file change
  useEffect(() => {
    if (fileType === 'docx' && !editContent && !contentLoadedRef.current) {
      contentLoadedRef.current = true;
      loadDocxFromStream();
    }
  }, [fileType, fileData.FileID, editContent, loadDocxFromStream]);

  /*
    Text content is loaded on VIEW, not only on edit.

    The fetch lived exclusively in `handleEditStart`, so a .txt (and, before it
    moved to the sheet grid, a .csv) rendered an empty editor until the user
    pressed Edit. Reading a file should not require asking to change it.
  */
  useEffect(() => {
    if (fileType !== 'text' || editContent || !fileData.decryptedBlobUrl) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(fileData.decryptedBlobUrl!);
        if (!response.ok) throw new Error(`Failed to fetch text file: ${response.status}`);
        const text = await response.text();
        if (!cancelled) setEditContent(text);
      } catch (error) {
        console.error('[Text View Error]:', error);
      }
    })();
    return () => { cancelled = true; };
  }, [fileType, fileData.decryptedBlobUrl, editContent]);

  // Auto-enable view mode for viewable file types
  useEffect(() => {
    if (canView && !editMode && !isLoadingContent && editContent) {
      setEditMode(true);
    }
  }, [canView, editMode, isLoadingContent, editContent]);

  const handleEditStart = useCallback(async () => {
    // Excel files don't need pre-loading - they'll load directly from blob URL
    if (fileType === 'excel') {
      setEditMode(true);
      setIsLoadingContent(false);
      return;
    }

    // If we already have content loaded, just enter edit mode
    if (editContent) {
      setEditMode(true);
      setIsLoadingContent(false);
      return;
    }

    // Load content for other file types
    if (fileData.decryptedBlobUrl && fileType !== 'docx' && fileType !== 'excel') {
      try {
        const response = await fetch(fileData.decryptedBlobUrl);
        if (!response.ok) throw new Error('Failed to fetch file');

        if (fileType === 'text') {
          const text = await response.text();
          setEditContent(text);
        }
        setEditMode(true);
        setIsLoadingContent(false);
      } catch (error) {
        console.error('Error loading content:', error);
        setIsLoadingContent(false);
      }
    }
  }, [fileData.decryptedBlobUrl, fileType, editContent]);

  // Auto-trigger edit mode for editable file types
  useEffect(() => {
    if (isLoadingContent && !editMode) {
      handleEditStart();
    }
  }, [isLoadingContent, editMode, handleEditStart]);

  const handleSave = useCallback(async () => {
    if (!onSave || !editContent) return;

    // Stream save in background without showing loading state
    try {
      let contentToSave = editContent;

      // For DOCX files, convert HTML back to DOCX binary format
      if (fileType === 'docx') {
        try {
          const docxBlob = await htmlToDocx(editContent, fileData.FileName);
          const base64Content = await blobToBase64(docxBlob);
          contentToSave = base64Content;
        } catch (conversionError) {
          console.error('Error converting HTML to DOCX:', conversionError);
          throw new Error('Failed to convert document to DOCX format');
        }
      } else if (fileType === 'excel' && fileData.FileName.toLowerCase().endsWith('.csv')) {
        // A .csv stays a .csv: the grid's content already IS comma-separated
        // text, so writing an XLSX payload under a .csv name would corrupt it.
        contentToSave = editContent;
      } else if (fileType === 'excel') {
        // An XLSX is rebuilt from EVERY sheet the grid holds, in workbook
        // order — reading the active sheet's CSV back would drop the rest.
        try {
          const XLSX = await import('@e965/xlsx');
          const workbook = XLSX.utils.book_new();
          const names = excelSheetOrder.length > 0 ? excelSheetOrder : Object.keys(excelSheets);
          if (names.length > 0) {
            names.forEach((name) => {
              XLSX.utils.book_append_sheet(
                workbook,
                XLSX.utils.aoa_to_sheet(excelSheets[name] || []),
                name
              );
            });
          } else {
            // Nothing tracked (content handed in as CSV) — parse what we have.
            const parsed = XLSX.read(editContent, { type: 'string' });
            parsed.SheetNames.forEach((name: string) => {
              XLSX.utils.book_append_sheet(workbook, parsed.Sheets[name], name);
            });
          }
          const excelBlob = new Blob(
            [XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })],
            { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
          );
          const base64Content = await blobToBase64(excelBlob);
          contentToSave = base64Content;
        } catch (conversionError) {
          console.error('Error converting sheet data to Excel:', conversionError);
          throw new Error('Failed to convert spreadsheet to Excel format');
        }
      }

      // Stream save without blocking the editor or showing loading state
      await onSave(contentToSave, fileData.FileName);

      // For files that should always be in edit mode, don't exit edit mode
      if (isPdf) {
        setEditMode(false);
        setEditContent('');
      }
      setExcelHasChanges(false);
      // For other file types, keep the user in edit mode for seamless editing
    } catch (error) {
      console.error('Error saving file:', error);
    }
  }, [onSave, editContent, fileData.FileName, fileType, isPdf, excelSheets, excelSheetOrder]);

  const handleSaveUnsavedChanges = useCallback(async () => {
    await handleSave();
    setShowUnsavedModal(false);
    if (onClose) {
      onClose();
    }
  }, [handleSave, onClose]);

  const handleDiscardChanges = useCallback(() => {
    setExcelHasChanges(false);
    setShowUnsavedModal(false);
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  if (isLoading) {
    return <FileTypeSkeleton fileType={fileType as 'docx' | 'pdf' | 'excel' | 'text' | 'unknown'} />;
  }

  /*
    ONE SCROLLER, AT THE PAGE'S RIGHT EDGE (files-4).

    There used to be two stacked: this root scrolled AND the inner column
    scrolled, so the bar the reader reached for belonged to the narrow document
    column while the page carried a second one behind it. Now the root is the
    single scroll owner, it spans the full width of the viewer region, and
    everything inside it lays out at its natural height. `scrollbar-gutter`
    keeps that gutter reserved so appearing content cannot shift the page.

    The PDF is the one exception: react-pdf-viewer is a virtualised scroller of
    its own, so it takes the full height and scrolls itself — still exactly one
    bar, still at the right edge.
  */
  return (
    <div
      className={`flex flex-col bg-bg w-full h-full ${
        isPdf ? 'overflow-hidden' : 'overflow-y-auto overflow-x-hidden custom-scrollbar-main'
      }`}
      style={isPdf ? undefined : { scrollbarGutter: 'stable' }}
    >
      <div className={`flex flex-col w-full ${isPdf ? 'flex-1 min-h-0' : 'min-h-full'}`}>
        {isLoadingContent && !editContent && !fileData.decryptedBlobUrl && fileType !== 'pdf' ? (
          // Show skeleton while loading (skip for PDF since it loads directly)
          <FileTypeSkeleton fileType={fileType as 'docx' | 'pdf' | 'excel' | 'text' | 'unknown'} />
        ) : isPdf && fileData.decryptedBlobUrl ? (
          // PDF viewer - always use SafePdfViewer for consistent, secure rendering
          <SafePdfViewer
            pdfUrl={fileData.decryptedBlobUrl}
            filename={fileData.FileName}
            zoom={zoom}
          />
        ) : fileType === 'excel' && fileData.decryptedBlobUrl ? (
          // The sheet grid IS the editor (files-18) — one surface for reading
          // and for editing, so view and edit mode render the same thing.
          <ExcelEditor
            blobUrl={fileData.decryptedBlobUrl}
            onContentChange={setEditContent}
            onSheetsChange={(sheets, order) => {
              setExcelSheets(sheets);
              setExcelSheetOrder(order);
              // The first publish is the load itself, not an edit.
              if (excelPublishedRef.current) setExcelHasChanges(true);
              excelPublishedRef.current = true;
            }}
            fileName={fileData.FileName}
            onSave={onSave ? handleSave : undefined}
            zoom={zoom}
          />
        ) : canView && (fileData.decryptedBlobUrl || editContent || fileType === 'docx') ? (
          // Unified editor for DOCX and TXT only (not PDF)
          <UnifiedDocumentEditor
            content={editContent}
            fileType={fileType as 'docx' | 'text'}
            onContentChange={setEditContent}
            onSave={handleSave}
            zoom={zoom}
          />
        ) : (
          // Fallback for unsupported file types
          !canView && fileType === 'unknown' && (
            <div className="flex items-center justify-center w-full h-full py-16">
              <div className="text-center">
                <p className="text-fg-muted mb-4">
                  {t('File type not supported')}
                </p>
              </div>
            </div>
          )
        )}
      </div>

      {/* Unsaved Changes Modal */}
      <UnsavedChangesModal
        isOpen={showUnsavedModal}
        onSave={handleSaveUnsavedChanges}
        onDiscard={handleDiscardChanges}
      />
    </div>
  );
};

export default SecureFileViewer;
